import React, { useState, useEffect, useCallback } from 'react';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import {
  MapPin,
  XCircle,
  Loader2,
  AlertCircle,
  Building2,
  Eye,
  EyeOff,
  Star,
  Navigation,
  Home,
  ChevronDown,
  ChevronUp,
  Map,
  Edit2,
  Save,
  X,
  Target,
  Check,
  Trash2,
  Plus,
  Truck,
  Compass,
  Power
} from 'lucide-react';
import { useBusinessLocation } from '../../../authentication_components/businessLocationContext';
import { useMobileLocation } from '../../../authentication_components/businessMobileLocationContext';
import { useAdminTracker } from '../../../authentication_components/adminTrackerContext';
import { useAuth } from '../../../authentication_components/authContext';

// =================================================================
// Google Maps Configuration
// =================================================================
const GOOGLE_MAPS_API_KEY = "AIzaSyBG8nQzX8m4ZSw9wYepF2idmupWAzVHbBM";

const mapContainerStyle = {
  width: '100%',
  height: '400px',
  borderRadius: '0.5rem'
};

const defaultCenter = {
  lat: 18.2208,
  lng: -66.5901
};

// =================================================================
// UUID Generator
// =================================================================
const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

// =================================================================
// Default Create Form State
// =================================================================
const defaultCreateForm = {
  location_address_1: '',
  location_address_2: '',
  location_city: '',
  location_zip: '',
  location_country: 'Puerto Rico',
  location_scenery_type: '',
  location_latitude: 0,
  location_longitude: 0,
  location_main: false,
  location_visibility: true,
};

// =================================================================
// Mobile Service Region Labels (Spanish)
// =================================================================
const REGION_LABELS_ES = {
  covers_north: 'Norte',
  covers_east: 'Este',
  covers_west: 'Oeste',
  covers_south: 'Sur',
  covers_north_east: 'Noreste',
  covers_north_west: 'Noroeste',
  covers_south_east: 'Sureste',
  covers_south_west: 'Suroeste',
};

// =================================================================
// Safe Tracking Helper
// =================================================================
const safeTrack = async (createTracker, adminId, trackingData) => {
  if (!adminId) {
    console.warn('[AdminTracker] Skipping tracking - adminId not available');
    return;
  }
  try {
    await createTracker({ admin_id: adminId, ...trackingData });
    console.log('[AdminTracker] Successfully tracked:', trackingData.change_type);
  } catch (trackerErr) {
    console.error('[AdminTracker] Failed to track action:', trackerErr);
  }
};

// =================================================================
// MobileServiceSection — handles the business_mobile_location 1:1 record
// =================================================================
const MobileServiceSection = ({ business,  onActionSuccess, onEditingChange }) => {
  const {
    tryGetMobileLocation,
    upsertMobileLocation,
    deleteMobileLocation,
    REGION_KEYS,
    getDefaultMobileLocation,
    loading: mlLoading,
    error: mlError,
    clearError: clearMlError,
  } = useMobileLocation();

  const { createTracker } = useAdminTracker();
  const { adminId } = useAuth();

  // State
  const [data, setData] = useState(null);
  const [exists, setExists] = useState(false);
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [localError, setLocalError] = useState(null);

  // Notify parent when editing state changes (used to disable Close button)
  useEffect(() => {
    if (onEditingChange) onEditingChange(editing);
  }, [editing, onEditingChange]);

  // ----------------------------------------------------------------
  // Fetch on business change
  // ----------------------------------------------------------------
  const fetchMobileData = useCallback(async () => {
    if (!business?.business_registry_number) return;
    setLocalError(null);
    setInitialFetchDone(false);
    try {
      const result = await tryGetMobileLocation(business.business_registry_number);
      if (result?.data) {
        setData(result.data);
        setExists(true);
      } else {
        setData(null);
        setExists(false);
      }
    } catch (err) {
      setLocalError(err.message || 'Error al cargar configuración móvil');
    } finally {
      setInitialFetchDone(true);
    }
  }, [business, tryGetMobileLocation]);

  useEffect(() => {
    if (business?.business_registry_number) {
      fetchMobileData();
    }
  }, [business, fetchMobileData]);

  // ----------------------------------------------------------------
  // Edit handlers
  // ----------------------------------------------------------------
  const startEditing = useCallback(() => {
    const baseForm = data || getDefaultMobileLocation(business.business_registry_number);
    setEditForm({ ...baseForm });
    setEditing(true);
    setExpanded(true);
    setLocalError(null);
    clearMlError();
  }, [data, business, getDefaultMobileLocation, clearMlError]);

  const cancelEditing = useCallback(() => {
    setEditing(false);
    setEditForm(null);
    setLocalError(null);
  }, []);

  const handleFieldToggle = useCallback((field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const saveChanges = useCallback(async () => {
    if (!editForm) return;
    setLocalError(null);

    try {
      const wasNew = !exists;
      const result = await upsertMobileLocation(business.business_registry_number, editForm);

      await safeTrack(createTracker, adminId, {
        change_type: wasNew ? 'mobile_location_create' : 'mobile_location_update',
        change_reason: `${wasNew ? 'Created' : 'Updated'} mobile service config for business ${business.business_registry_number} (${business.business_name})`
      });

      if (result?.data) {
        setData(result.data);
      } else {
        setData(editForm);
      }
      setExists(true);
      setEditing(false);
      setEditForm(null);
      onActionSuccess && onActionSuccess('Configuración de servicio móvil actualizada exitosamente');
    } catch (err) {
      setLocalError(err.message || 'Error al guardar configuración móvil');
    }
  }, [editForm, exists, business, upsertMobileLocation, createTracker, adminId, onActionSuccess]);

  // ----------------------------------------------------------------
  // Delete handler — clears the entire mobile service record
  // ----------------------------------------------------------------
  const handleDelete = useCallback(async () => {
    if (!window.confirm('¿Está seguro de que desea eliminar la configuración de servicio móvil? Esta acción no se puede deshacer.')) return;
    setLocalError(null);
    try {
      await deleteMobileLocation(business.business_registry_number);
      await safeTrack(createTracker, adminId, {
        change_type: 'mobile_location_delete',
        change_reason: `Deleted mobile service config for business ${business.business_registry_number} (${business.business_name})`
      });
      setData(null);
      setExists(false);
      setEditing(false);
      setEditForm(null);
      onActionSuccess && onActionSuccess('Configuración de servicio móvil eliminada');
    } catch (err) {
      setLocalError(err.message || 'Error al eliminar configuración móvil');
    }
  }, [business, deleteMobileLocation, createTracker, adminId, onActionSuccess]);

  // ----------------------------------------------------------------
  // Derived state for display
  // ----------------------------------------------------------------
  const isMobileEnabled = data?.is_mobile_service || false;
  const coversAll = data?.covers_all || false;
  const activeRegions = data
    ? REGION_KEYS.filter(k => data[k]).map(k => REGION_LABELS_ES[k])
    : [];
  const coverageCount = coversAll ? REGION_KEYS.length : activeRegions.length;
  const coveragePercent = coversAll ? 100 : Math.round((activeRegions.length / REGION_KEYS.length) * 100);

  // ----------------------------------------------------------------
  // Render
  // ----------------------------------------------------------------
  if (!initialFetchDone) {
    return (
      <div className="mb-6 border border-cyan-200 rounded-xl bg-cyan-50 p-4 flex items-center justify-center">
        <Loader2 className="h-5 w-5 text-cyan-600 animate-spin mr-2" />
        <span className="text-sm text-cyan-700">Cargando configuración de servicio móvil...</span>
      </div>
    );
  }

  return (
    <div className={`mb-6 border-2 rounded-xl overflow-hidden transition-all ${
      editing ? 'border-cyan-400 bg-cyan-50' : 'border-cyan-200 bg-white'
    }`}>
      {/* Header — clickable to expand/collapse when not editing */}
      <button
        type="button"
        onClick={() => !editing && setExpanded(prev => !prev)}
        disabled={editing}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-cyan-50 transition-colors disabled:cursor-default disabled:hover:bg-transparent"
      >
        <div className="flex items-center gap-3">
          <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${
            isMobileEnabled ? 'bg-cyan-600 text-white' : 'bg-gray-200 text-gray-500'
          }`}>
            <Truck className="h-5 w-5" />
          </div>
          <div className="text-left">
            <h4 className="font-semibold text-gray-800 flex items-center gap-2">
              Servicio Móvil
              {exists && isMobileEnabled && (
                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                  Activo
                </span>
              )}
              {exists && !isMobileEnabled && (
                <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                  Inactivo
                </span>
              )}
              {!exists && (
                <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-medium rounded-full">
                  Sin configurar
                </span>
              )}
            </h4>
            <p className="text-xs text-gray-500 mt-0.5">
              {!exists
                ? 'No se ha configurado servicio móvil para este negocio'
                : !isMobileEnabled
                  ? 'Servicio móvil deshabilitado'
                  : coversAll
                    ? 'Cubre toda la isla'
                    : activeRegions.length === 0
                      ? 'Sin regiones de cobertura seleccionadas'
                      : `${activeRegions.length} región${activeRegions.length === 1 ? '' : 'es'} de cobertura`}
            </p>
          </div>
        </div>
        {!editing && (
          expanded
            ? <ChevronUp className="h-5 w-5 text-gray-400" />
            : <ChevronDown className="h-5 w-5 text-gray-400" />
        )}
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-cyan-200 bg-white">
          {/* Local error */}
          {(localError || mlError) && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{localError || mlError}</p>
            </div>
          )}

          {/* ── EMPTY STATE ── */}
          {!exists && !editing && (
            <div className="mt-4 text-center py-6">
              <div className="mx-auto w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mb-3">
                <Truck className="h-8 w-8 text-cyan-600" />
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Este negocio aún no tiene configurado el servicio móvil.
              </p>

                <button
                  type="button"
                  onClick={startEditing}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg
                           hover:bg-cyan-700 transition-colors text-sm font-medium"
                >
                  <Plus className="h-4 w-4" />
                  Configurar Servicio Móvil
                </button>

            </div>
          )}

          {/* ── VIEW MODE ── */}
          {exists && !editing && (
            <div className="mt-4 space-y-4">
              {/* Status row */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">
                    Estado del Servicio
                  </label>
                  <p className={`text-sm font-medium flex items-center gap-1.5 ${
                    isMobileEnabled ? 'text-green-700' : 'text-gray-500'
                  }`}>
                    <Power className="h-3.5 w-3.5" />
                    {isMobileEnabled ? 'Habilitado' : 'Deshabilitado'}
                  </p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">
                    Cobertura
                  </label>
                  <p className="text-sm font-medium text-gray-800 flex items-center gap-1.5">
                    <Compass className="h-3.5 w-3.5 text-cyan-600" />
                    {coversAll
                      ? 'Toda la isla'
                      : `${coverageCount}/${REGION_KEYS.length} regiones (${coveragePercent}%)`}
                  </p>
                </div>
              </div>

              {/* Region grid (read-only) */}
              {isMobileEnabled && !coversAll && (
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">
                    Regiones Cubiertas
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {REGION_KEYS.map(key => {
                      const active = data[key];
                      return (
                        <div
                          key={key}
                          className={`px-2 py-1.5 rounded-md text-xs font-medium text-center border ${
                            active
                              ? 'bg-cyan-100 text-cyan-800 border-cyan-300'
                              : 'bg-gray-50 text-gray-400 border-gray-200'
                          }`}
                        >
                          {REGION_LABELS_ES[key]}
                          {active && <Check className="h-3 w-3 inline ml-1" />}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {coversAll && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                  <p className="text-sm text-green-800 font-medium">
                    Este negocio ofrece servicio móvil en toda la isla.
                  </p>
                </div>
              )}

              {/* Action buttons */}
   
                <div className="flex gap-2 pt-3 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={startEditing}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-cyan-50
                             hover:bg-cyan-100 text-cyan-700 rounded-lg transition-colors text-sm font-medium"
                  >
                    <Edit2 className="h-4 w-4" />
                    Editar Configuración
                  </button>
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={mlLoading}
                    className="px-3 py-2 bg-red-50 hover:bg-red-100 text-red-700
                             rounded-lg transition-colors text-sm font-medium disabled:opacity-50"
                    title="Eliminar configuración de servicio móvil"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

            </div>
          )}

          {/* ── EDIT MODE ── */}
          {editing && editForm && (
            <div className="mt-4 space-y-4">
              <div className="flex items-center gap-2">
                <Edit2 className="h-4 w-4 text-cyan-600" />
                <h5 className="font-semibold text-gray-800 text-sm">
                  {exists ? 'Editando Servicio Móvil' : 'Configurando Servicio Móvil'}
                </h5>
              </div>

              {/* Master toggle: is_mobile_service */}
              <label className={`flex items-center justify-between p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                editForm.is_mobile_service
                  ? 'border-cyan-300 bg-cyan-50'
                  : 'border-gray-200 bg-white'
              }`}>
                <div className="flex items-center gap-3">
                  <Power className={`h-5 w-5 ${editForm.is_mobile_service ? 'text-cyan-600' : 'text-gray-400'}`} />
                  <div>
                    <p className="text-sm font-medium text-gray-800">Servicio Móvil Disponible</p>
                    <p className="text-xs text-gray-500">El negocio ofrece servicio en la ubicación del cliente</p>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={editForm.is_mobile_service}
                  onChange={(e) => handleFieldToggle('is_mobile_service', e.target.checked)}
                  className="h-5 w-5 rounded text-cyan-600 focus:ring-cyan-500"
                />
              </label>

              {/* Coverage settings (only when is_mobile_service is true) */}
              {editForm.is_mobile_service && (
                <>
                  {/* covers_all toggle */}
                  <label className={`flex items-center justify-between p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                    editForm.covers_all
                      ? 'border-green-300 bg-green-50'
                      : 'border-gray-200 bg-white'
                  }`}>
                    <div className="flex items-center gap-3">
                      <Compass className={`h-5 w-5 ${editForm.covers_all ? 'text-green-600' : 'text-gray-400'}`} />
                      <div>
                        <p className="text-sm font-medium text-gray-800">Cubre Toda la Isla</p>
                        <p className="text-xs text-gray-500">Activa esto para cubrir todas las regiones</p>
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={editForm.covers_all}
                      onChange={(e) => handleFieldToggle('covers_all', e.target.checked)}
                      className="h-5 w-5 rounded text-green-600 focus:ring-green-500"
                    />
                  </label>

                  {/* Individual region grid */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Regiones Específicas
                      {editForm.covers_all && (
                        <span className="ml-2 text-gray-400 italic">(deshabilitado: cubre toda la isla)</span>
                      )}
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {REGION_KEYS.map(key => {
                        const active = editForm[key];
                        const disabled = editForm.covers_all;
                        return (
                          <button
                            key={key}
                            type="button"
                            onClick={() => !disabled && handleFieldToggle(key, !active)}
                            disabled={disabled}
                            className={`px-3 py-2 text-sm font-medium rounded-lg border-2 transition-all flex items-center justify-between ${
                              disabled
                                ? 'bg-gray-50 text-gray-300 border-gray-200 cursor-not-allowed'
                                : active
                                  ? 'bg-cyan-600 text-white border-cyan-600 shadow-sm'
                                  : 'bg-white text-gray-700 border-gray-300 hover:border-cyan-400'
                            }`}
                          >
                            <span>{REGION_LABELS_ES[key]}</span>
                            {active && !disabled && <Check className="h-3.5 w-3.5" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}

              {/* Action buttons */}
              <div className="flex gap-2 pt-3 border-t border-gray-200">
                <button
                  type="button"
                  onClick={cancelEditing}
                  disabled={mlLoading}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg
                           hover:bg-gray-50 transition-colors text-sm font-medium disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={saveChanges}
                  disabled={mlLoading}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-cyan-600
                           text-white rounded-lg hover:bg-cyan-700 transition-colors text-sm font-medium
                           disabled:opacity-50"
                >
                  {mlLoading ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Guardando...</>
                  ) : (
                    <><Save className="h-4 w-4" /> Guardar Configuración</>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// =================================================================
// BusinessLocationDialog Component
// =================================================================
const BusinessLocationDialog = ({ isOpen, onClose, business }) => {
  const {
    getLocationsByRegistryNumber,
    createLocation,
    updateLocation,
    deleteLocation,
    loading,
    error,
    clearError,
    formatLocationData
  } = useBusinessLocation();

  const { createTracker } = useAdminTracker();
  const { adminId, loginType } = useAuth();

  const { isLoaded: isMapLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY
  });

  // Core state
  const [locations, setLocations] = useState([]);
  const [expandedLocationId, setExpandedLocationId] = useState(null);
  const [localError, setLocalError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  // Edit state
  const [editingLocationId, setEditingLocationId] = useState(null);
  const [editFormData, setEditFormData] = useState({});

  // Create state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createFormData, setCreateFormData] = useState(defaultCreateForm);
  const [createFormError, setCreateFormError] = useState(null);

  // Mobile section editing state — lifted up so we can disable Close button
  const [mobileSectionEditing, setMobileSectionEditing] = useState(false);

  // Map state — shared between edit and create modes
  const [showMapDialog, setShowMapDialog] = useState(false);
  const [mapTarget, setMapTarget] = useState(null); // 'edit' | 'create'
  const [tempCoordinates, setTempCoordinates] = useState(null);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapError, setMapError] = useState(null);



  // ----------------------------------------------------------------
  // Lifecycle
  // ----------------------------------------------------------------
  useEffect(() => {
    if (isOpen && business?.business_registry_number) {
      fetchLocations();
    }
  }, [isOpen, business]);

  useEffect(() => {
    if (!isOpen) {
      setLocations([]);
      setExpandedLocationId(null);
      setEditingLocationId(null);
      setEditFormData({});
      setShowCreateForm(false);
      setCreateFormData(defaultCreateForm);
      setCreateFormError(null);
      setLocalError(null);
      setSuccessMessage('');
      setMobileSectionEditing(false);
      clearError();
    }
  }, [isOpen, clearError]);

  // ----------------------------------------------------------------
  // Fetch
  // ----------------------------------------------------------------
  const fetchLocations = useCallback(async () => {
    if (!business?.business_registry_number) {
      setLocalError('No se encontró el número de registro del negocio.');
      return;
    }
    setLocalError(null);
    setSuccessMessage('');
    try {
      const result = await getLocationsByRegistryNumber(business.business_registry_number);
      if (result.data && result.data.length > 0) {
        setLocations(result.data);
        if (result.data.length === 1) setExpandedLocationId(result.data[0].location_id);
        await safeTrack(createTracker, adminId, {
          change_type: 'location_view',
          change_reason: `Viewed ${result.data.length} location(s) for business ${business.business_registry_number} (${business.business_name})`
        });
      } else {
        setLocations([]);
      }
    } catch (err) {
      setLocalError(err.message || 'Error al cargar las ubicaciones.');
    }
  }, [business, getLocationsByRegistryNumber, createTracker, adminId]);

  // ----------------------------------------------------------------
  // Mobile section success handler
  // ----------------------------------------------------------------
  const handleMobileActionSuccess = useCallback((msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 3000);
  }, []);

  // ----------------------------------------------------------------
  // Expand / Collapse
  // ----------------------------------------------------------------
  const toggleExpand = useCallback((locationId) => {
    setExpandedLocationId(prev => prev === locationId ? null : locationId);
  }, []);

  // ----------------------------------------------------------------
  // Edit handlers
  // ----------------------------------------------------------------
  const startEditing = useCallback((location) => {
    setShowCreateForm(false);
    setEditingLocationId(location.location_id);
    setEditFormData({
      location_address_1: location.location_address_1 || '',
      location_address_2: location.location_address_2 || '',
      location_city: location.location_city || '',
      location_zip: location.location_zip || '',
      location_scenery_type: location.location_scenery_type || '',
      location_latitude: location.location_latitude || 0,
      location_longitude: location.location_longitude || 0,
      location_main: location.location_main || false,
      location_visibility: location.location_visibility || false,
    });
    setSuccessMessage('');
    setLocalError(null);
  }, []);

  const cancelEditing = useCallback(() => {
    setEditingLocationId(null);
    setEditFormData({});
    setLocalError(null);
  }, []);

  const handleEditChange = useCallback((field, value) => {
    setEditFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const saveLocationChanges = useCallback(async () => {
    if (!editingLocationId) return;
    setLocalError(null);
    setSuccessMessage('');
    try {
      const updateData = {
        location_address_1: editFormData.location_address_1,
        location_address_2: editFormData.location_address_2 || '',
        location_city: editFormData.location_city,
        location_zip: editFormData.location_zip,
        location_scenery_type: editFormData.location_scenery_type || '',
        location_latitude: parseFloat(editFormData.location_latitude) || 0,
        location_longitude: parseFloat(editFormData.location_longitude) || 0,
        location_main: editFormData.location_main,
        location_visibility: editFormData.location_visibility,
      };
      await updateLocation(editingLocationId, updateData);
      await safeTrack(createTracker, adminId, {
        change_type: 'location_update',
        change_reason: `Updated location ${editingLocationId} for business ${business.business_registry_number} (${business.business_name})`
      });
      setSuccessMessage('Ubicación actualizada exitosamente');
      setEditingLocationId(null);
      setEditFormData({});
      await fetchLocations();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setLocalError(err.message || 'Error al actualizar la ubicación.');
    }
  }, [editingLocationId, editFormData, business, updateLocation, createTracker, adminId, fetchLocations]);

  // ----------------------------------------------------------------
  // Create handlers
  // ----------------------------------------------------------------
  const openCreateForm = useCallback(() => {
    setEditingLocationId(null);
    setEditFormData({});
    setExpandedLocationId(null);
    setCreateFormData(defaultCreateForm);
    setCreateFormError(null);
    setShowCreateForm(true);
  }, []);

  const cancelCreateForm = useCallback(() => {
    setShowCreateForm(false);
    setCreateFormData(defaultCreateForm);
    setCreateFormError(null);
  }, []);

  const handleCreateChange = useCallback((field, value) => {
    setCreateFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const saveNewLocation = useCallback(async () => {
    setCreateFormError(null);
    setSuccessMessage('');

    // Basic validation
    if (!createFormData.location_address_1.trim()) {
      setCreateFormError('La dirección principal es requerida.');
      return;
    }
    if (!createFormData.location_city.trim()) {
      setCreateFormError('La ciudad es requerida.');
      return;
    }
    if (!createFormData.location_zip.trim()) {
      setCreateFormError('El código postal es requerido.');
      return;
    }

    try {
      const newLocation = {
        location_id: generateUUID(),
        business_registry_number: business.business_registry_number,
        location_address_1: createFormData.location_address_1.trim(),
        location_address_2: createFormData.location_address_2.trim(),
        location_city: createFormData.location_city.trim(),
        location_country: createFormData.location_country || 'Puerto Rico',
        location_zip: createFormData.location_zip.trim(),
        location_scenery_type: createFormData.location_scenery_type || '',
        location_latitude: parseFloat(createFormData.location_latitude) || 0,
        location_longitude: parseFloat(createFormData.location_longitude) || 0,
        location_main: createFormData.location_main,
        location_visibility: createFormData.location_visibility,
        location_count: locations.length + 1,
      };

      await createLocation(newLocation);

      await safeTrack(createTracker, adminId, {
        change_type: 'location_create',
        change_reason: `Created new location for business ${business.business_registry_number} (${business.business_name})`
      });

      setSuccessMessage('Ubicación creada exitosamente');
      setShowCreateForm(false);
      setCreateFormData(defaultCreateForm);
      await fetchLocations();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setCreateFormError(err.message || 'Error al crear la ubicación.');
    }
  }, [createFormData, business, createLocation, createTracker, adminId, fetchLocations, locations.length]);

  // ----------------------------------------------------------------
  // Delete handler
  // ----------------------------------------------------------------
  const handleDeleteLocation = useCallback(async (locationId) => {
    if (!window.confirm('¿Está seguro de que desea eliminar esta ubicación? Esta acción no se puede deshacer.')) return;
    setLocalError(null);
    setSuccessMessage('');
    try {
      await deleteLocation(locationId);
      await safeTrack(createTracker, adminId, {
        change_type: 'location_delete',
        change_reason: `Deleted location ${locationId} for business ${business.business_registry_number} (${business.business_name})`
      });
      setSuccessMessage('Ubicación eliminada exitosamente');
      await fetchLocations();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setLocalError(err.message || 'Error al eliminar la ubicación.');
    }
  }, [business, deleteLocation, createTracker, adminId, fetchLocations]);

  // ----------------------------------------------------------------
  // Map handlers — shared for edit and create
  // ----------------------------------------------------------------
  const openMapDialog = useCallback((target) => {
    setMapTarget(target);
    setShowMapDialog(true);

    const formData = target === 'edit' ? editFormData : createFormData;
    if (formData.location_latitude && formData.location_longitude) {
      setTempCoordinates({ latitude: formData.location_latitude, longitude: formData.location_longitude });
      setMapCenter({ lat: formData.location_latitude, lng: formData.location_longitude });
    } else {
      setTempCoordinates(null);
      setMapCenter(defaultCenter);
    }
    setMapError(null);
  }, [editFormData, createFormData]);

  const closeMapDialog = useCallback(() => {
    setShowMapDialog(false);
    setMapTarget(null);
    setMapError(null);
  }, []);

  const handleMapClick = useCallback((e) => {
    setTempCoordinates({ latitude: e.latLng.lat(), longitude: e.latLng.lng() });
  }, []);

  const confirmMapSelection = useCallback(() => {
    if (tempCoordinates) {
      if (mapTarget === 'edit') {
        setEditFormData(prev => ({
          ...prev,
          location_latitude: tempCoordinates.latitude,
          location_longitude: tempCoordinates.longitude,
        }));
      } else {
        setCreateFormData(prev => ({
          ...prev,
          location_latitude: tempCoordinates.latitude,
          location_longitude: tempCoordinates.longitude,
        }));
      }
    }
    closeMapDialog();
  }, [tempCoordinates, mapTarget, closeMapDialog]);

  const fetchCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setMapError('La geolocalización no es soportada por este navegador.');
      return;
    }
    setMapLoading(true);
    setMapError(null);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const newCoords = { latitude: position.coords.latitude, longitude: position.coords.longitude };
        setTempCoordinates(newCoords);
        setMapCenter({ lat: newCoords.latitude, lng: newCoords.longitude });
        setMapLoading(false);
      },
      (error) => {
        let msg = 'Ocurrió un error al obtener la ubicación.';
        if (error.code === error.PERMISSION_DENIED) msg = 'Permiso de ubicación denegado.';
        else if (error.code === error.POSITION_UNAVAILABLE) msg = 'Información de ubicación no disponible.';
        else if (error.code === error.TIMEOUT) msg = 'La solicitud de ubicación expiró.';
        setMapError(msg);
        setMapLoading(false);
      }
    );
  }, []);

  // ----------------------------------------------------------------
  // Open in Maps
  // ----------------------------------------------------------------
  const openInMaps = useCallback((location) => {
    const { location_latitude, location_longitude } = location;
    if (location_latitude && location_longitude) {
      window.open(`https://www.google.com/maps?q=${location_latitude},${location_longitude}`, '_blank');
    }
  }, []);

  const handleClose = () => {
    if (!loading && !editingLocationId && !showCreateForm && !mobileSectionEditing) onClose();
  };

  if (!isOpen || !business) return null;

  const establishmentTypes = [
    'Local Comercial', 'Plaza Comercial', 'Centro Comercial', 'Kiosco',
    'Food Truck', 'Casa/Residencia', 'Oficina', 'Almacén', 'Otro'
  ];

  // ----------------------------------------------------------------
  // Shared coordinate field used by both edit and create forms
  // ----------------------------------------------------------------
  const CoordinateField = ({ formData, target }) => (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-2">
        Coordenadas GPS
      </label>
      <div className="space-y-2">
        <button
          type="button"
          onClick={() => openMapDialog(target)}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-orange-50
                   text-orange-700 border border-orange-200 rounded-lg hover:bg-orange-100
                   transition-colors text-sm font-medium"
        >
          <Map className="h-4 w-4" />
          {formData.location_latitude && formData.location_longitude
            ? 'Actualizar Coordenadas en Mapa'
            : 'Seleccionar en Mapa'}
        </button>
        {formData.location_latitude !== 0 && formData.location_longitude !== 0 && (
          <div className="p-2 bg-green-50 border border-green-200 rounded text-xs">
            <p className="font-mono text-green-800">
              Lat: {Number(formData.location_latitude).toFixed(6)},
              Lng: {Number(formData.location_longitude).toFixed(6)}
            </p>
          </div>
        )}
      </div>
    </div>
  );

  // ----------------------------------------------------------------
  // Render
  // ----------------------------------------------------------------
  return (
    <>
      <div
        className="fixed inset-0 bg-gray-900 bg-opacity-70 flex items-center justify-center z-[70] p-4"
        onClick={handleClose}
      >
        <div
          className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-5 border-b border-gray-100 flex justify-between items-center bg-gradient-to-r from-blue-50 to-indigo-50">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <MapPin className="h-5 w-5 text-blue-500" />
              Ubicaciones del Negocio

                <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                  Modo Edición
                </span>

            </h3>
            <button
              onClick={handleClose}
              disabled={loading || editingLocationId !== null || showCreateForm || mobileSectionEditing}
              className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
            >
              <XCircle className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Business Info */}
            <div className="mb-6 text-center pb-4 border-b border-gray-200">
              <h4 className="font-semibold text-gray-900 text-lg">{business.business_name}</h4>
              <p className="text-xs text-gray-500 font-mono mt-1">{business.business_registry_number}</p>
              <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-full">
                <Building2 className="h-4 w-4 text-gray-600" />
                <span className="font-medium text-gray-800">{business.business_owner}</span>
              </div>
            </div>

            {/* Success Message */}
            {successMessage && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                <Check className="h-5 w-5 text-green-600 flex-shrink-0" />
                <p className="text-sm text-green-800 font-medium">{successMessage}</p>
              </div>
            )}

            {/* ── MOBILE SERVICE SECTION ── */}
            <MobileServiceSection
              business={business}
              onActionSuccess={handleMobileActionSuccess}
              onEditingChange={setMobileSectionEditing}
            />

            {/* Section divider */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-px bg-gray-200"></div>
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Ubicaciones Físicas
              </span>
              <div className="flex-1 h-px bg-gray-200"></div>
            </div>

            {/* Loading State */}
            {loading && !editingLocationId && !showCreateForm && (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="h-10 w-10 text-blue-500 animate-spin mb-4" />
                <p className="text-gray-600">Cargando ubicaciones...</p>
              </div>
            )}

            {/* Error State */}
            {(localError || error) && !loading && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{localError || error}</p>
              </div>
            )}

            {/* ── CREATE NEW LOCATION FORM ── */}
            {showCreateForm && (
              <div className="mb-6 border-2 border-dashed border-green-300 rounded-xl bg-green-50 p-5">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                    <Plus className="h-4 w-4 text-green-600" />
                    Nueva Ubicación
                  </h4>
                  <span className="text-xs text-gray-500 bg-white border border-gray-200 rounded px-2 py-0.5">
                    {locations.length + 1}ª ubicación
                  </span>
                </div>

                {createFormError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-800">{createFormError}</p>
                  </div>
                )}

                <div className="space-y-4 bg-white rounded-lg p-4 border border-green-200">
                  {/* Address 1 */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Dirección Principal <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={createFormData.location_address_1}
                      onChange={(e) => handleCreateChange('location_address_1', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      placeholder="Calle, número, urbanización..."
                    />
                  </div>

                  {/* Address 2 */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Dirección Secundaria
                    </label>
                    <input
                      type="text"
                      value={createFormData.location_address_2}
                      onChange={(e) => handleCreateChange('location_address_2', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      placeholder="Apartamento, suite, local..."
                    />
                  </div>

                  {/* City + ZIP */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Ciudad <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={createFormData.location_city}
                        onChange={(e) => handleCreateChange('location_city', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        placeholder="Ciudad"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Código Postal <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={createFormData.location_zip}
                        onChange={(e) => handleCreateChange('location_zip', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        placeholder="00901"
                      />
                    </div>
                  </div>

                  {/* Country */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      País
                    </label>
                    <input
                      type="text"
                      value={createFormData.location_country}
                      onChange={(e) => handleCreateChange('location_country', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      placeholder="Puerto Rico"
                    />
                  </div>

                  {/* Scenery Type */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Tipo de Establecimiento
                    </label>
                    <select
                      value={createFormData.location_scenery_type}
                      onChange={(e) => handleCreateChange('location_scenery_type', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    >
                      <option value="">Seleccione el tipo</option>
                      {establishmentTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  {/* Coordinates */}
                  <CoordinateField formData={createFormData} target="create" />

                  {/* Toggles */}
                  <div className="space-y-2 pt-2 border-t border-gray-200">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={createFormData.location_main}
                        onChange={(e) => handleCreateChange('location_main', e.target.checked)}
                        className="rounded text-green-600 focus:ring-green-500"
                      />
                      <Star className="h-4 w-4 text-yellow-500" />
                      <span className="text-gray-700">Marcar como ubicación principal</span>
                    </label>

                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={createFormData.location_visibility}
                        onChange={(e) => handleCreateChange('location_visibility', e.target.checked)}
                        className="rounded text-green-600 focus:ring-green-500"
                      />
                      {createFormData.location_visibility
                        ? <Eye className="h-4 w-4 text-green-500" />
                        : <EyeOff className="h-4 w-4 text-gray-400" />}
                      <span className="text-gray-700">Visible públicamente</span>
                    </label>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 pt-4 border-t border-gray-200">
                    <button
                      onClick={cancelCreateForm}
                      disabled={loading}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg
                               hover:bg-gray-50 transition-colors text-sm font-medium disabled:opacity-50"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={saveNewLocation}
                      disabled={loading}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600
                               text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium
                               disabled:opacity-50"
                    >
                      {loading ? (
                        <><Loader2 className="h-4 w-4 animate-spin" /> Guardando...</>
                      ) : (
                        <><Save className="h-4 w-4" /> Guardar Ubicación</>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* No Locations State */}
            {!loading && !error && !localError && locations.length === 0 && !showCreateForm && (
              <div className="text-center py-12">
                <div className="mx-auto w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <MapPin className="h-10 w-10 text-gray-400" />
                </div>
                <h4 className="text-lg font-semibold text-gray-700 mb-2">
                  Sin Ubicaciones Registradas
                </h4>
                <p className="text-sm text-gray-500 mb-6">
                  Este negocio aún no tiene ubicaciones físicas registradas en el sistema.
                </p>

                  <button
                    onClick={openCreateForm}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white
                             rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                  >
                    <Plus className="h-4 w-4" />
                    Agregar Primera Ubicación
                  </button>

              </div>
            )}

            {/* Locations List */}
            {!loading && locations.length > 0 && (
              <div className="space-y-3">
                {/* Location Count + Add Button */}
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm font-medium text-gray-700">
                    Total: <span className="text-blue-600 font-semibold">{locations.length}</span>
                    <span className="text-gray-400 ml-2 font-normal">
                      ({locations.filter(l => l.location_visibility).length} visible{locations.filter(l => l.location_visibility).length !== 1 ? 's' : ''})
                    </span>
                  </p>

                    <button
                      onClick={openCreateForm}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white
                               rounded-lg hover:bg-green-700 transition-colors text-xs font-medium"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Agregar Ubicación
                    </button>

                </div>

                {/* Location Cards */}
                {locations.map((location, index) => {
                  const formatted = formatLocationData(location);
                  const isExpanded = expandedLocationId === location.location_id;
                  const isEditing = editingLocationId === location.location_id;

                  return (
                    <div
                      key={location.location_id}
                      className={`border rounded-lg transition-all ${
                        location.location_main
                          ? 'border-blue-300 bg-blue-50'
                          : 'border-gray-200 bg-white'
                      }`}
                    >
                      {/* Card Header */}
                      <button
                        onClick={() => !isEditing && toggleExpand(location.location_id)}
                        disabled={isEditing}
                        className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-lg disabled:cursor-default disabled:hover:bg-transparent"
                      >
                        <div className="flex items-center gap-3 flex-1 text-left">
                          <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                            location.location_main ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'
                          }`}>
                            {index + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              {location.location_main && <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />}
                              <span className="font-semibold text-gray-900">{location.location_city}</span>
                              {!location.location_visibility && <EyeOff className="h-4 w-4 text-gray-400" />}
                            </div>
                            <p className="text-sm text-gray-600 truncate">{location.location_address_1}</p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {location.location_main && (
                              <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">Principal</span>
                            )}
                            {location.location_visibility
                              ? <Eye className="h-4 w-4 text-green-500" />
                              : <EyeOff className="h-4 w-4 text-gray-400" />}
                          </div>
                        </div>
                        {!isEditing && (
                          isExpanded
                            ? <ChevronUp className="h-5 w-5 text-gray-400 ml-2" />
                            : <ChevronDown className="h-5 w-5 text-gray-400 ml-2" />
                        )}
                      </button>

                      {/* Expanded View */}
                      {isExpanded && !isEditing && (
                        <div className="px-4 pb-4 border-t border-gray-200 bg-white rounded-b-lg">
                          <div className="mt-4 space-y-3">
                            <div>
                              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 block">Dirección Completa</label>
                              <p className="text-sm text-gray-800">{formatted.fullAddress}</p>
                            </div>

                            {(location.location_latitude !== 0 || location.location_longitude !== 0) && (
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 block">Latitud</label>
                                  <p className="text-sm font-mono text-gray-800">{location.location_latitude.toFixed(8)}</p>
                                </div>
                                <div>
                                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 block">Longitud</label>
                                  <p className="text-sm font-mono text-gray-800">{location.location_longitude.toFixed(8)}</p>
                                </div>
                              </div>
                            )}

                            {location.location_scenery_type && (
                              <div>
                                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 block">Tipo de Local</label>
                                <p className="text-sm text-gray-800">{location.location_scenery_type}</p>
                              </div>
                            )}

                            <div className="flex gap-2 flex-wrap">
                              {location.location_main && (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                                  <Star className="h-3 w-3" /> Ubicación Principal
                                </span>
                              )}
                              {location.location_visibility ? (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                                  <Eye className="h-3 w-3" /> Visible
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                                  <EyeOff className="h-3 w-3" /> Oculta
                                </span>
                              )}
                            </div>

                            <div className="pt-3 border-t border-gray-100 flex gap-2">
                              {(location.location_latitude !== 0 || location.location_longitude !== 0) && (
                                <button
                                  onClick={() => openInMaps(location)}
                                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-50
                                           hover:bg-blue-100 text-blue-700 rounded-lg transition-colors text-sm font-medium"
                                >
                                  <Map className="h-4 w-4" /> Abrir en Maps
                                </button>
                              )}

                                <>
                                  <button
                                    onClick={() => startEditing(location)}
                                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-purple-50
                                             hover:bg-purple-100 text-purple-700 rounded-lg transition-colors text-sm font-medium"
                                  >
                                    <Edit2 className="h-4 w-4" /> Editar
                                  </button>
                                  {!location.location_main && (
                                    <button
                                      onClick={() => handleDeleteLocation(location.location_id)}
                                      className="px-3 py-2 bg-red-50 hover:bg-red-100 text-red-700
                                               rounded-lg transition-colors text-sm font-medium"
                                      title="Eliminar ubicación"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  )}
                                </>

                            </div>

                            <div className="pt-2 border-t border-gray-100">
                              <p className="text-xs text-gray-400 font-mono">ID: {location.location_id}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Edit Mode */}
                      {isEditing && (
                        <div className="px-4 pb-4 border-t border-gray-200 bg-white rounded-b-lg">
                          <div className="mt-4 space-y-4">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                                <Edit2 className="h-4 w-4 text-purple-600" /> Editando Ubicación
                              </h4>
                            </div>

                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Dirección Principal *</label>
                              <input
                                type="text"
                                value={editFormData.location_address_1}
                                onChange={(e) => handleEditChange('location_address_1', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                placeholder="Calle, número, urbanización..."
                              />
                            </div>

                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Dirección Secundaria</label>
                              <input
                                type="text"
                                value={editFormData.location_address_2}
                                onChange={(e) => handleEditChange('location_address_2', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                placeholder="Apartamento, suite, local..."
                              />
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Ciudad *</label>
                                <input
                                  type="text"
                                  value={editFormData.location_city}
                                  onChange={(e) => handleEditChange('location_city', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Código Postal *</label>
                                <input
                                  type="text"
                                  value={editFormData.location_zip}
                                  onChange={(e) => handleEditChange('location_zip', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                  placeholder="00901"
                                />
                              </div>
                            </div>

                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Tipo de Establecimiento</label>
                              <select
                                value={editFormData.location_scenery_type}
                                onChange={(e) => handleEditChange('location_scenery_type', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                              >
                                <option value="">Seleccione el tipo</option>
                                {establishmentTypes.map(type => (
                                  <option key={type} value={type}>{type}</option>
                                ))}
                              </select>
                            </div>

                            <CoordinateField formData={editFormData} target="edit" />

                            <div className="space-y-2 pt-2 border-t border-gray-200">
                              <label className="flex items-center gap-2 text-sm">
                                <input
                                  type="checkbox"
                                  checked={editFormData.location_main}
                                  onChange={(e) => handleEditChange('location_main', e.target.checked)}
                                  className="rounded text-purple-600 focus:ring-purple-500"
                                />
                                <Star className="h-4 w-4 text-yellow-500" />
                                <span className="text-gray-700">Marcar como ubicación principal</span>
                              </label>
                              <label className="flex items-center gap-2 text-sm">
                                <input
                                  type="checkbox"
                                  checked={editFormData.location_visibility}
                                  onChange={(e) => handleEditChange('location_visibility', e.target.checked)}
                                  className="rounded text-purple-600 focus:ring-purple-500"
                                />
                                {editFormData.location_visibility
                                  ? <Eye className="h-4 w-4 text-green-500" />
                                  : <EyeOff className="h-4 w-4 text-gray-400" />}
                                <span className="text-gray-700">Visible públicamente</span>
                              </label>
                            </div>

                            <div className="flex gap-2 pt-4 border-t border-gray-200">
                              <button
                                onClick={cancelEditing}
                                disabled={loading}
                                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg
                                         hover:bg-gray-50 transition-colors text-sm font-medium disabled:opacity-50"
                              >
                                Cancelar
                              </button>
                              <button
                                onClick={saveLocationChanges}
                                disabled={loading}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-purple-600
                                         text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium
                                         disabled:opacity-50"
                              >
                                {loading ? (
                                  <><Loader2 className="h-4 w-4 animate-spin" /> Guardando...</>
                                ) : (
                                  <><Save className="h-4 w-4" /> Guardar Cambios</>
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          {!loading && !editingLocationId && !showCreateForm && !mobileSectionEditing && (
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={handleClose}
                className="w-full py-2 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg
                         font-medium transition-colors text-sm"
              >
                Cerrar
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Map Selection Dialog */}
      {showMapDialog && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                <Map className="h-5 w-5 text-orange-500 mr-2" />
                Seleccionar Coordenadas
              </h3>
              <button onClick={closeMapDialog} className="text-gray-400 hover:text-gray-600">
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-4 relative flex-grow overflow-y-auto">
              <p className="text-sm text-gray-600 mb-4">
                Use el botón para obtener su ubicación actual o haga clic en el mapa para colocar el marcador manualmente.
              </p>

              <div className="rounded-lg overflow-hidden border border-gray-300 relative">
                {isMapLoaded ? (
                  <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    center={mapCenter}
                    zoom={14}
                    onClick={handleMapClick}
                    options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}
                  >
                    {tempCoordinates && (
                      <Marker position={{ lat: tempCoordinates.latitude, lng: tempCoordinates.longitude }} />
                    )}
                  </GoogleMap>
                ) : (
                  <div className="h-[400px] flex items-center justify-center bg-gray-100 text-gray-500">
                    <Loader2 className="h-8 w-8 animate-spin mr-2" /> Cargando Mapa...
                  </div>
                )}

                <button
                  onClick={fetchCurrentLocation}
                  disabled={mapLoading}
                  className="absolute top-4 right-4 bg-white text-gray-700 px-4 py-2 rounded-lg shadow-lg text-sm
                           font-medium border border-gray-200 flex items-center hover:bg-gray-50 transition-colors
                           disabled:opacity-75"
                >
                  {mapLoading
                    ? <Loader2 className="h-4 w-4 animate-spin mr-2 text-orange-500" />
                    : <Target className="h-4 w-4 mr-2 text-orange-500" />}
                  Obtener mi ubicación
                </button>
              </div>

              {mapError && (
                <div className="mt-3 p-3 bg-red-50 text-red-700 text-sm rounded-lg flex items-start">
                  <AlertCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                  {mapError}
                </div>
              )}

              {tempCoordinates && (
                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
                  <p className="font-medium text-green-800 mb-1">Coordenadas seleccionadas:</p>
                  <p className="font-mono text-green-700">
                    Lat: {tempCoordinates.latitude.toFixed(8)}, Lng: {tempCoordinates.longitude.toFixed(8)}
                  </p>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-200 flex justify-end gap-3 bg-gray-50">
              <button
                onClick={closeMapDialog}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100
                         transition-colors text-sm font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={confirmMapSelection}
                disabled={!tempCoordinates}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors
                         text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                <Check className="h-4 w-4 mr-2" />
                Confirmar Ubicación
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BusinessLocationDialog;